/**
 * Участники проекта.
 *
 * Действия зависят от роли: наблюдатель видит состав, но не меняет его.
 * Владелец из списка не удаляется и через смену роли не понижается —
 * передача владения будет отдельной подтверждаемой операцией.
 */

import {
  Loader2Icon,
  MailPlusIcon,
  MoreHorizontalIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { MemberRole, ProjectMemberDto } from '@project-os/contracts';
import { MEMBER_ROLE_LABELS_RU } from '@project-os/contracts';
import {
  useAddMember,
  useMemberCandidates,
  useProjectMembers,
  useRemoveMember,
  useUpdateMemberRole,
} from '@/api/members';
import { ApiError } from '@/api/client';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/states';
import { StatusBadge } from '@/components/common/status-badge';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRelative } from '@/lib/format';
import { InviteMemberDialog } from './InviteMemberDialog';
import { useProjectContext } from './use-project-context';

const ROLE_TONES: Record<MemberRole, 'success' | 'progress' | 'neutral'> = {
  OWNER: 'success',
  ADMIN: 'progress',
  MEMBER: 'neutral',
  VIEWER: 'neutral',
};

const ASSIGNABLE_ROLES: Exclude<MemberRole, 'OWNER'>[] = ['ADMIN', 'MEMBER', 'VIEWER'];

const ROLE_HINTS: Record<MemberRole, string> = {
  OWNER: 'Полный доступ, управление проектом и участниками',
  ADMIN: 'Управление содержимым и участниками',
  MEMBER: 'Создание и изменение проектной информации',
  VIEWER: 'Только чтение',
};

function AddMemberDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<Exclude<MemberRole, 'OWNER'>>('MEMBER');
  const candidates = useMemberCandidates(projectId, open);
  const addMember = useAddMember(projectId);

  const available = (candidates.data ?? []).filter((candidate) => !candidate.alreadyMember);

  const handleSubmit = async () => {
    try {
      await addMember.mutateAsync({ userId, role });
      toast.success('Участник добавлен');
      setUserId('');
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось добавить участника');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Добавить участника</DialogTitle>
          <DialogDescription>
            В проект можно добавить только участника того же рабочего пространства. Если нужного
            человека в системе ещё нет, закройте это окно и нажмите «Пригласить».
          </DialogDescription>
        </DialogHeader>

        {candidates.isPending ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" />
            Загружаем участников пространства
          </div>
        ) : candidates.isError ? (
          <ErrorState error={candidates.error} onRetry={() => void candidates.refetch()} />
        ) : available.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            Все участники рабочего пространства уже в проекте.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="member-user">Пользователь</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger id="member-user" className="w-full">
                  <SelectValue placeholder="Выберите пользователя" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((candidate) => (
                    <SelectItem key={candidate.user.id} value={candidate.user.id}>
                      {candidate.user.name} · {candidate.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="member-role">Роль в проекте</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as Exclude<MemberRole, 'OWNER'>)}
              >
                <SelectTrigger id="member-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {MEMBER_ROLE_LABELS_RU[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{ROLE_HINTS[role]}</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button disabled={!userId || addMember.isPending} onClick={() => void handleSubmit()}>
            {addMember.isPending ? <Loader2Icon className="animate-spin" /> : null}
            Добавить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberActions({
  member,
  projectId,
  canManageRoles,
}: {
  member: ProjectMemberDto;
  projectId: string;
  canManageRoles: boolean;
}) {
  const updateRole = useUpdateMemberRole(projectId);
  const removeMember = useRemoveMember(projectId);

  if (!canManageRoles || member.role === 'OWNER') return null;

  const changeRole = async (role: Exclude<MemberRole, 'OWNER'>) => {
    try {
      await updateRole.mutateAsync({ memberId: member.id, role });
      toast.success('Роль изменена');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить роль');
    }
  };

  const remove = async () => {
    try {
      await removeMember.mutateAsync(member.id);
      toast.success('Участник удалён из проекта');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось удалить участника');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-7" aria-label="Действия с участником">
          <MoreHorizontalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
          Роль в проекте
        </DropdownMenuLabel>
        {ASSIGNABLE_ROLES.map((role) => (
          <DropdownMenuItem
            key={role}
            disabled={role === member.role || updateRole.isPending}
            onSelect={() => void changeRole(role)}
          >
            {MEMBER_ROLE_LABELS_RU[role]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          disabled={removeMember.isPending}
          onSelect={() => void remove()}
        >
          Удалить из проекта
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MembersPage() {
  const project = useProjectContext();
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: members, isPending, isError, error, refetch } = useProjectMembers(project.id);

  const canAdd = project.permissions.includes('MEMBER_ADD');
  const canManageRoles = project.permissions.includes('MEMBER_ROLE_CHANGE');

  return (
    <>
      <PageHeader
        title="Участники"
        description="Кто работает над проектом и за что отвечает"
        actions={
          canAdd ? (
            <div className="flex items-center gap-2">
              {/* «Добавить» берёт человека из пространства, «Пригласить»
                  заводит нового. Приглашение реже и необратимее, поэтому
                  стоит вторым и оформлено спокойнее. */}
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <UserPlusIcon />
                Добавить
              </Button>
              <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)}>
                <MailPlusIcon />
                Пригласить
              </Button>
            </div>
          ) : null
        }
      />

      <PageBody>
        <div className="surface-card overflow-hidden border border-border/50">
          {isPending ? (
            <TableSkeleton rows={3} columns={5} />
          ) : isError ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : members.length === 0 ? (
            <EmptyState
              icon={<UsersIcon />}
              title="Участников пока нет"
              description="Добавьте коллег из рабочего пространства, чтобы распределить ответственность."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Участник</TableHead>
                  <TableHead className="w-36">Роль</TableHead>
                  <TableHead className="hidden lg:table-cell">Направления</TableHead>
                  <TableHead className="w-28 text-right">Открытых</TableHead>
                  <TableHead className="hidden w-40 md:table-cell">Активность</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="align-top">
                      <div className="flex items-start gap-2.5 py-0.5">
                        <UserAvatar user={member.user} size="md" className="mt-0.5" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{member.user.name}</span>
                            {/* Приглашение выдано, но человек ещё не заходил.
                                Без этой отметки он неотличим от работающего
                                участника, и непонятно, кого ждать, а кому
                                выдать ссылку заново. */}
                            {member.user.status === 'INVITED' ? (
                              <StatusBadge
                                label="Приглашён"
                                tone="warning"
                                title="Ссылка выдана, пароль ещё не установлен"
                              />
                            ) : null}
                            {member.user.status === 'DISABLED' ? (
                              <StatusBadge
                                label="Отключён"
                                tone="danger"
                                title="Учётная запись отключена: вход невозможен"
                              />
                            ) : null}
                          </div>
                          {member.profile.title ? (
                            <div className="truncate text-xs">{member.profile.title}</div>
                          ) : null}
                          <div className="truncate text-xs text-muted-foreground">
                            {member.user.email}
                            {member.profile.location ? ` · ${member.profile.location}` : ''}
                          </div>
                          {member.profile.contact ? (
                            <div className="truncate text-2xs text-muted-foreground">
                              Связь: {member.profile.contact}
                            </div>
                          ) : null}
                          {member.profile.bio ? (
                            <p className="mt-1 max-w-md text-2xs leading-relaxed text-muted-foreground/80">
                              {member.profile.bio}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        label={MEMBER_ROLE_LABELS_RU[member.role]}
                        tone={ROLE_TONES[member.role]}
                        title={ROLE_HINTS[member.role]}
                      />
                    </TableCell>
                    <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">
                      {member.ownedWorkstreams.length > 0
                        ? member.ownedWorkstreams.map((w) => w.name).join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {member.openItemCount}
                    </TableCell>
                    <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                      {formatRelative(member.lastActivityAt)}
                    </TableCell>
                    <TableCell>
                      <MemberActions
                        member={member}
                        projectId={project.id}
                        canManageRoles={canManageRoles}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {!canAdd ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Ваша роль в проекте — {MEMBER_ROLE_LABELS_RU[project.role].toLowerCase()}. Изменение
            состава участников доступно владельцу и администраторам.
          </p>
        ) : null}
      </PageBody>

      <AddMemberDialog open={addOpen} onOpenChange={setAddOpen} projectId={project.id} />

      <InviteMemberDialog open={inviteOpen} onOpenChange={setInviteOpen} projectId={project.id} />
    </>
  );
}
